import type { PrimerEstimate, PrimerSideEstimate } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { isFailedSide } from './estimationCore.ts'

const PRIMER_SIDES = ['forward', 'reverse'] as const
type PrimerSide = (typeof PRIMER_SIDES)[number]

const percent = (share: number): string => `${Math.round(share * 100)}%`

/** How the reads gave one side's primer, or why they gave none, in plain words: only what the estimate measured. */
export const sideBasis = (estimate: PrimerSideEstimate, side: PrimerSide): string => {
  const carrying = `the reads carrying the ${side} primer`
  if (isFailedSide(estimate)) {
    return 'its reads could not be read for an estimate'
  }
  if (estimate.primer) {
    if (!estimate.prefix) {
      return `no tag: the ${side} primer starts ${percent(estimate.support)} of the reads carrying it`
    }
    return `tag ${estimate.prefix}${estimate.spacer ? ', behind a variable spacer,' : ''} found in ${percent(estimate.support)} of ${carrying}`
  }
  if (estimate.support > 0) {
    return `no tag dominates its reads (mixed tags): the most common is in only ${percent(estimate.support)} of ${carrying}`
  }
  if (estimate.located === 0) {
    return `the ${side} primer was not found on its reads`
  }
  if (estimate.located !== undefined) {
    return `no tag dominates its reads (too few reads): the ${side} primer was found on only ${estimate.located} reads`
  }
  return `no tag estimated: ${estimate.reason}`
}

export const CONFIDENCE_TEXT = { high: 'high confidence', low: 'low confidence', none: 'no estimate' } as const

type Shortfall = 'mixed' | 'not-found' | 'too-few'

const shortfallOf = (estimate: PrimerSideEstimate): Shortfall | undefined => {
  if (isFailedSide(estimate) || estimate.primer) {
    return undefined
  }
  if (estimate.support > 0) {
    return 'mixed'
  }
  if (estimate.located === 0) {
    return 'not-found'
  }
  return estimate.located === undefined ? undefined : 'too-few'
}

/** One clause for both sides when they fell short the same way. */
const pairedBasis = ({ forward, reverse }: PrimerEstimate): string | undefined => {
  const kind = shortfallOf(forward)
  if (!kind || kind !== shortfallOf(reverse)) {
    return undefined
  }
  if (kind === 'not-found') {
    return 'neither primer was found on its reads'
  }
  if (kind === 'too-few') {
    return forward.located === reverse.located
      ? `no tag dominates its reads (too few reads): the primers were found on only ${forward.located} reads`
      : `no tag dominates its reads (too few reads): the forward primer was found on only ${forward.located} reads, the reverse primer on only ${reverse.located}`
  }
  const [f, r] = [percent(forward.support), percent(reverse.support)]
  return f === r
    ? `no tag dominates its reads (mixed tags): the most common tag on either side is in only ${f} of the reads carrying that primer`
    : `no tag dominates its reads (mixed tags): the most common forward tag is in only ${f} of the reads carrying the forward primer, the most common reverse tag in ${r}`
}

const sidesBasis = (estimate: PrimerEstimate, sides: readonly PrimerSide[], describe: (side: PrimerSide) => string): string[] => {
  const paired = sides.length === PRIMER_SIDES.length ? pairedBasis(estimate) : undefined
  return paired ? [paired] : [...new Set(sides.map(describe))]
}

/** Both sides' basis, once when they say the same. */
export const estimateLines = (estimate: PrimerEstimate): string[] =>
  sidesBasis(estimate, PRIMER_SIDES, (side) => sideBasis(estimate[side], side))

/** Why the reads gave an item no primers to write; undefined without an estimate yet. */
export const estimateShortfall = (estimate: PrimerEstimate | undefined): string | undefined => {
  if (!estimate) {
    return undefined
  }
  const short = PRIMER_SIDES.filter((side) => estimate[side].confidence !== 'high')
  return sidesBasis(estimate, short.length > 0 ? short : PRIMER_SIDES, (side) => {
    const basis = sideBasis(estimate[side], side)
    return estimate[side].confidence === 'low' ? `${basis}, too few to be sure` : basis
  }).join('; ')
}

export const ESTIMATE_NEXT_STEP = 'Enter its primers, tag included, or add a sample sheet with them.'
